import { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { DailyRouteGraphByDayFilter } from "../fragments.dispatches/DailyRouteGraphByDayFilter";

// ── Legend data ──────────────────────────────────────────────────────────────
const LEGEND = [
    { key: "grayThumbtacks",      label: "Dados de baja",                   color: "#D3D3D3", textDark: true  },
    { key: "blueThumbtacks",      label: "Pendientes",                      color: "#303F9F", textDark: false },
    { key: "redThumbtacks",       label: "Sin compra",                      color: "#D32F2F", textDark: false },
    { key: "greenThumbtacks",     label: "Autoventa — Vendidos",            color: "#07bc0c", textDark: false },
    { key: "yellowThumbtacks",    label: "Preventas — Pendientes",          color: "#FBCB0A", textDark: true  },
    { key: "skyBlueThumbtacks",   label: "Preventas — Enviado",             color: "#9AD0EC", textDark: true  },
    { key: "darkGreenThumbtacks", label: "Preventas — Entregado conforme",  color: "#125B50", textDark: false },
    { key: "fuchsiaThumbtacks",   label: "Preventas — Envío fallido",       color: "#f012be", textDark: false },
];

const PIE_COLORS = LEGEND.map((l) => l.color);

export function DailyRouteGraphByDayPage() {
    const pieChartRef = useRef(null);

    const [dailyRouteData, setDailyRouteData] = useState({
        allThumbtacks: 0, grayThumbtacks: 0, blueThumbtacks: 0,
        greenThumbtacks: 0, redThumbtacks: 0, yellowThumbtacks: 0,
        skyBlueThumbtacks: 0, darkGreenThumbtacks: 0, fuchsiaThumbtacks: 0,
        all_thumbtacks: 1,
    });

    useEffect(() => {
        const pieChart = new Chart(pieChartRef.current, {
            type: "pie",
            data: {
                labels: LEGEND.map((l) => l.label),
                datasets: [{
                    data: LEGEND.map((l) => dailyRouteData[l.key] ?? 0),
                    backgroundColor: PIE_COLORS,
                    borderWidth: 2,
                    borderColor: "#fff",
                }],
            },
            options: {
                responsive: true,
                plugins: {
                    legend: { position: "bottom", labels: { padding: 16, font: { size: 11 } } },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const val = ctx.raw;
                                const pct = dailyRouteData.all_thumbtacks > 0
                                    ? ((val / dailyRouteData.all_thumbtacks) * 100).toFixed(1)
                                    : "0.0";
                                return ` ${ctx.label}: ${val} (${pct}%)`;
                            },
                        },
                    },
                },
            },
        });
        return () => pieChart.destroy();
    }, [dailyRouteData]);

    const total = dailyRouteData.all_thumbtacks;
    const pct   = (val) => total > 0 ? ((val / total) * 100).toFixed(1) : "0.0";
    const hasData = total > 1; // initial state has all_thumbtacks=1 as placeholder

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Ventas por ruta
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">Reportes</li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">/</li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">Gráfico por día</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-full px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

                        {/* Filter panel */}
                        <div className="lg:col-span-3">
                            <DailyRouteGraphByDayFilter setDailyRouteData={setDailyRouteData} />
                        </div>

                        {/* Stats table */}
                        <div className="lg:col-span-4">
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <i className="fas fa-table text-blue-500"></i>
                                        Detalle por estado
                                    </h2>
                                    {hasData && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {dailyRouteData.allThumbtacks} clientes totales
                                        </p>
                                    )}
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm border-separate border-spacing-0">
                                        <thead>
                                            <tr>
                                                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 w-6"></th>
                                                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 text-left">Descripción</th>
                                                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 text-center">Cant.</th>
                                                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 text-center">%</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {LEGEND.map(({ key, label, color, textDark }) => {
                                                const val = dailyRouteData[key] ?? 0;
                                                return (
                                                    <tr key={key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                                        <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                                                            <span
                                                                className="inline-block h-4 w-4 rounded-sm flex-shrink-0"
                                                                style={{ backgroundColor: color }}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700">
                                                            {label}
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-center font-bold text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-700">
                                                            {val}
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-center text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                                            {pct(val)}%
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-gray-50 dark:bg-gray-800/50">
                                                <td className="px-3 py-2 border-t border-gray-200 dark:border-gray-600"></td>
                                                <td className="px-3 py-2 text-xs font-bold text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-600 uppercase tracking-wide">
                                                    Total activos
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center font-bold text-gray-800 dark:text-gray-200 border-t border-gray-200 dark:border-gray-600">
                                                    {total}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center font-bold text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-600">
                                                    100%
                                                </td>
                                            </tr>
                                            <tr className="bg-gray-50 dark:bg-gray-800/50">
                                                <td className="px-3 py-2 border-b border-gray-200 dark:border-gray-600"></td>
                                                <td className="px-3 py-2 text-xs font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 uppercase tracking-wide">
                                                    Total clientes
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center font-bold text-blue-600 dark:text-blue-400 border-b border-gray-200 dark:border-gray-600">
                                                    {dailyRouteData.allThumbtacks}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                                                    100%
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Pie chart */}
                        <div className="lg:col-span-5">
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <i className="fas fa-chart-pie text-blue-500"></i>
                                        Distribución por estado
                                    </h2>
                                </div>
                                <div className="p-5 flex items-center justify-center">
                                    <div className="w-full max-w-sm">
                                        <canvas ref={pieChartRef} />
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </>
    );
}
